import { ErsFormProp }              from '@app/modules/ers/forms/ers-form/ers-form.model';
import {
	ErsDeviceTypeEnum,
	ErsExternalStatusEnum,
	ErsStatusEnum
}                                   from '@app/modules/ers/models/ers.model';
import { APP_CONFIG }               from '@appConfig/_app-config_master';
import { DeviceTypeEnum }           from '@appConfig/media-config';
import { ITabContent }              from '@core/interfaces/common.interfaces';
import {
	FormDataTreatmentEnum,
	IFormBaseData,
	IFormCreateData,
	IFormUpdateData
}                                   from '@core/interfaces/form.interface';
import { environment }              from '@env/environment';
import { EntityVariantEnum }        from '@models/entity.model';
import {
	ErsScopesEnum,
	FeatureScopesEnum,
	ScopePermissionCheckEnum
}                                   from '@models/scope.enums';
import { ActionEnum }               from '@shared/components/action-item/action-item.enum';
import {
	ButtonGroupItemType,
	IButtonGroupConfig
}                                   from '@shared/components/button-group/button-group.model';
import {
	IStatusFlow,
	OutOfFlowDisplayModeEnum
}                                   from '@shared/components/status-flow/status-flow.model';
import {
	FormValidatorConfigType,
	FormValidatorIdentifierEnum,
	FormValidatorsConfig,
	FormValidatorVariantEnum
}                                   from '@shared/forms/models/form.model';
import { SortOrderEnum }            from '@shared/list-operations/components/sort-filter/sort-filter.model';
import { LIST_OPERATIONS_METADATA } from '@shared/list-operations/config/list-operations.config';
import {
	FilterIdentifiersEnum,
	FilterTypes,
	IFilter,
	IFilterItem
}                                   from '@shared/list-operations/model/filter.model';
import {
	IListOperationsConfig,
	ListOperationsIdentifierEnum
}                                   from '@shared/list-operations/model/list-operations.model';
import { PagingModeEnum }           from '@shared/list-operations/model/paging.model';
import { actionStatus }             from '@shared/services/toaster.service';
import { ObjectUtils }              from '@utils/helpers';


export interface IErsMetadata {
	buttonGroupConfigs: {
		list: IButtonGroupConfig;
	};
	entityId: string;
	details: {
		dateFormat: Intl.DateTimeFormatOptions;
		internalTabContent: ITabContent[];
	};
	form: {
		baseData: IFormBaseData;
		createData: IFormCreateData[];
		updateData: IFormUpdateData[];
		preserveData: IFormUpdateData[];
		validatorsErs: FormValidatorsConfig<ErsFormProp>;
		validatorsErsExternal: FormValidatorsConfig<ErsFormProp>;
	};
	i18n: {
		basepath: string;
		page: string;
		tab: string;
		entity: string;
		form: string;
		details: string;
		ersLifecycleModal: string;
	};
	listOperations: IListOperationsConfig;
	listOperationsExternal: IListOperationsConfig;
	listOperationsBusinessPremisesErs: IListOperationsConfig;
	listOperationsBusinessPremisesErsExternal: IListOperationsConfig;
	status: IStatusFlow<ErsStatusEnum, ErsScopesEnum>;
	statusErsExternal: IStatusFlow<ErsExternalStatusEnum, ErsScopesEnum>;
	supportedEntityVariants: EntityVariantEnum[];
	storeId: string;
}

const MASTER_LIST_OPERATIONS_CONFIG: IListOperationsConfig = {
	id: ListOperationsIdentifierEnum.ERS_LIST_PAGE,
	pagingData: {
		mode: PagingModeEnum.Light,
		continuesPagesMode: false,
		defaultValues: {
			pageSize: LIST_OPERATIONS_METADATA.default.paging.pageSize ?? 20,
			pageNumber: LIST_OPERATIONS_METADATA.default.paging.pageNumber ?? 0
		}
	},
	searchData: {
		placeholderI18nPath: 'ers.search.placeholder'
	},
	sortData: [
		{ name: 'ersIdentifier', i18nPath: 'ers.form.ersIdentifier.label' },
		{ name: 'ersSerialNumber', i18nPath: 'ers.form.ersSerialNumber.label' },
		{ name: 'detailedDescription', i18nPath: 'shared.forms.detailedDescription.label' },
		{ name: 'lastModifiedDate', i18nPath: 'ers.sort.lastModifiedDate.label', defaultActive: true, sortOrder: SortOrderEnum.DESC }
	],
	filterData: [
		{
			order: 1,
			isRendered: true,
			isDisabled: false,
			filterId: FilterIdentifiersEnum.FILTER,
			type: FilterTypes.FILTER_TEXT_TYPE,
			i18nPath: 'ers.filter.queryFilter',
			items: [
				{ i18nPath: 'ers.filter.queryFilter' }
			]
		},
		{
			order: 2,
			isRendered: true,
			isDisabled: false,
			filterId: FilterIdentifiersEnum.STATUS,
			type: FilterTypes.FILTER_LIST_TYPE,
			i18nPath: 'shared.forms.status',
			values: [ ErsStatusEnum.ACTIVE, ErsStatusEnum.INACTIVE, ErsStatusEnum.SUSPENDED ],
			items: [
				{ value: ErsStatusEnum.ACTIVE, i18nPath: 'shared.status.active' },
				{ value: ErsStatusEnum.DISABLED, i18nPath: 'shared.status.disabled' },
				{ value: ErsStatusEnum.INACTIVE, i18nPath: 'shared.status.inactive' },
				{ value: ErsStatusEnum.SUSPENDED, i18nPath: 'shared.status.suspended' }
			]
		},
		{
			order: 3,
			isRendered: true,
			isDisabled: false,
			filterId: FilterIdentifiersEnum.ERS_SERIAL_NUMBER,
			type: FilterTypes.FILTER_TEXT_TYPE,
			i18nPath: 'ers.filter.ersSerialNumber',
			items: [
				{ i18nPath: 'ers.filter.ersSerialNumber' }
			]
		},
		{
			order: 4,
			isRendered: true,
			isDisabled: false,
			filterId: FilterIdentifiersEnum.HAS_VALIDATION_RESULTS,
			type: FilterTypes.FILTER_SWITCH_TYPE,
			i18nPath: 'shared.filters.hasValidationResults'
		},
	]
};

// predicates for ELSTER indicator state check in forms
const isStatusActive =
	( key: string, status: ErsStatusEnum ): boolean => key === 'status' && status === ErsStatusEnum.ACTIVE;
const isStatusDisabled =
	( key: string, status: ErsStatusEnum ): boolean => key === 'status' && status === ErsStatusEnum.DISABLED;
const isDeviceTypeTaximeterOrDistanceCounter =
	( key: string, deviceType: ErsDeviceTypeEnum ): boolean => key === 'deviceType' &&  [ ErsDeviceTypeEnum.TAXIMETER, ErsDeviceTypeEnum.DISTANCE_COUNTER ].includes( deviceType );

const listButtonGroupItems: ButtonGroupItemType[] = [
	{ enforceLabelCustomTemplate: true, id: EntityVariantEnum.INTERNAL, order: 1 },
	{ enforceLabelCustomTemplate: true, id: EntityVariantEnum.EXTERNAL, order: 2 }
];

const listOperationsConfigErs = ( listOperationsIdentifier: ListOperationsIdentifierEnum ): IListOperationsConfig => {
	const filterWhiteList: FilterIdentifiersEnum[] = [
		FilterIdentifiersEnum.FILTER,
		FilterIdentifiersEnum.STATUS,
		FilterIdentifiersEnum.ERS_SERIAL_NUMBER,
		FilterIdentifiersEnum.HAS_VALIDATION_RESULTS
	];

	const filterData: IFilter[] = MASTER_LIST_OPERATIONS_CONFIG.filterData
	.filter( ( filter: IFilter ) => filterWhiteList.includes( filter.filterId ) );

	return {
		...MASTER_LIST_OPERATIONS_CONFIG,
		id: listOperationsIdentifier,
		filterData
	};
};

const listOperationsConfigErsExternal = ( listOperationsIdentifier: ListOperationsIdentifierEnum ): IListOperationsConfig => {
	const filterWhiteList: FilterIdentifiersEnum[] = [
		FilterIdentifiersEnum.FILTER,
		FilterIdentifiersEnum.STATUS,
		FilterIdentifiersEnum.ERS_SERIAL_NUMBER,
		FilterIdentifiersEnum.HAS_VALIDATION_RESULTS
	];

	const filterData: IFilter[] = MASTER_LIST_OPERATIONS_CONFIG.filterData
	.filter( ( filter: IFilter ) => filterWhiteList.includes( filter.filterId ) )
	.map( ( filter: IFilter ) => {
		const isStatusFilter: boolean = filter.filterId === FilterIdentifiersEnum.STATUS;


		// StatusFilter
		if ( isStatusFilter ) {
			const values: ErsExternalStatusEnum[] = [
				ErsExternalStatusEnum.ACTIVE,
				ErsExternalStatusEnum.INACTIVE
			];

			const items: IFilterItem[] = [
				{ value: ErsExternalStatusEnum.ACTIVE, i18nPath: 'shared.status.active' },
				{ value: ErsExternalStatusEnum.DISABLED, i18nPath: 'shared.status.disabled' },
				{ value: ErsExternalStatusEnum.INACTIVE, i18nPath: 'shared.status.inactive' }
			];

			return { ...filter, items, values };
		}

		return filter;
	} );

	return {
		...MASTER_LIST_OPERATIONS_CONFIG,
		id: listOperationsIdentifier,
		filterData
	};
};

const supportedEntityVariants: EntityVariantEnum[] = [
	EntityVariantEnum.INTERNAL
];

const validatorsErs: FormValidatorsConfig<ErsFormProp> = {
	acquisitionDate: new Map<FormValidatorIdentifierEnum, FormValidatorConfigType[]>( [
		[
			FormValidatorIdentifierEnum.DATE_RELATION,
			[
				{
					config: null,
					identifier: FormValidatorIdentifierEnum.DATE_RELATION,
					identifierChild: 'activationDateReference',
					isEnabled: false,
					variant: {
						conditionIsValid: null,
						type: FormValidatorVariantEnum.CONDITIONAL
					}
				},
				{
					config: null,
					identifier: FormValidatorIdentifierEnum.DATE_RELATION,
					identifierChild: 'currentDateReference',
					isEnabled: false,
					variant: {
						conditionIsValid: null,
						type: FormValidatorVariantEnum.CONDITIONAL
					}
				}
			]
		],
		[
			FormValidatorIdentifierEnum.REQUIRED,
			[
				{
					identifier: FormValidatorIdentifierEnum.REQUIRED,
					isEnabled: false,
					variant: {
						type: FormValidatorVariantEnum.INITIAL
					}
				}
			]
		]
	] ),
	brand: new Map<FormValidatorIdentifierEnum, FormValidatorConfigType[]>( [
		[
			FormValidatorIdentifierEnum.MAX_LENGTH,
			[
				{
					identifier: FormValidatorIdentifierEnum.MAX_LENGTH,
					isEnabled: true,
					length: 200,
					variant: {
						type: FormValidatorVariantEnum.INITIAL
					}
				}
			]
		],
		[
			FormValidatorIdentifierEnum.REQUIRED,
			[
				{
					identifier: FormValidatorIdentifierEnum.REQUIRED,
					isEnabled: false,
					variant: {
						type: FormValidatorVariantEnum.INITIAL
					}
				}
			]
		]
	] ),
	commissioningDate: new Map<FormValidatorIdentifierEnum, FormValidatorConfigType[]>( [
		// Todo validations are not finally specified yet
		[
			FormValidatorIdentifierEnum.DATE_RELATION,
			[
				{
					config: null,
					identifier: FormValidatorIdentifierEnum.DATE_RELATION,
					identifierChild: 'acquisitionDateReference',
					isEnabled: true,
					variant: {
						conditionIsValid: null,
						type: FormValidatorVariantEnum.CONDITIONAL
					}
				},
				{
					config: null,
					identifier: FormValidatorIdentifierEnum.DATE_RELATION,
					identifierChild: 'currentDateReference',
					isEnabled: true,
					variant: {
						conditionIsValid: null,
						type: FormValidatorVariantEnum.CONDITIONAL
					}
				},
				{
					config: null,
					identifier: FormValidatorIdentifierEnum.DATE_RELATION,
					identifierChild: 'decommissionDateReference',
					isEnabled: true,
					variant: {
						conditionIsValid: null,
						type: FormValidatorVariantEnum.CONDITIONAL
					}
				},
		// 		{
		// 			config: null,
		// 			identifier: FormValidatorIdentifierEnum.DATE_RELATION,
		// 			identifierChild: 'firstConnectionEstablishedDateReference',
		// 			isEnabled: true,
		// 			variant: {
		// 				conditionIsValid: null,
		// 				type: FormValidatorVariantEnum.CONDITIONAL
		// 			}
		// 		}
			]
		],
		[
			FormValidatorIdentifierEnum.REQUIRED,
			[
				{
					identifier: FormValidatorIdentifierEnum.REQUIRED,
					isEnabled: false,
					variant: {
						conditionIsValid: null,
						type: FormValidatorVariantEnum.CONDITIONAL
					}
				}
			]
		]
	] ),
	deactivationDate: new Map<FormValidatorIdentifierEnum, FormValidatorConfigType[]>( [] ),
	deactivationReason: new Map<FormValidatorIdentifierEnum, FormValidatorConfigType[]>( [
		[
			FormValidatorIdentifierEnum.MAX_LENGTH,
			[
				{
					identifier: FormValidatorIdentifierEnum.MAX_LENGTH,
					isEnabled: true,
					length: 1000,
					variant: {
						conditionIsValid: null,
						type: FormValidatorVariantEnum.CONDITIONAL
					}
				}
			]
		],
		[
			FormValidatorIdentifierEnum.MIN_LENGTH,
			[
				{
					identifier: FormValidatorIdentifierEnum.MIN_LENGTH,
					isEnabled: true,
					length: 1,
					variant: {
						conditionIsValid: null,
						type: FormValidatorVariantEnum.CONDITIONAL
					}
				}
			]
		]
	] ),
	decommissionDate: new Map<FormValidatorIdentifierEnum, FormValidatorConfigType[]>( [
		[
			FormValidatorIdentifierEnum.DATE_RELATION,
			[
				{
					config: null,
					identifier: FormValidatorIdentifierEnum.DATE_RELATION,
					identifierChild: 'commissioningDateReference',
					isEnabled: true,
					variant: {
						conditionIsValid: null,
						type: FormValidatorVariantEnum.CONDITIONAL
					}
				},
				{
					config: null,
					identifier: FormValidatorIdentifierEnum.DATE_RELATION,
					identifierChild: 'currentDateReference',
					isEnabled: true,
					variant: {
						conditionIsValid: null,
						type: FormValidatorVariantEnum.CONDITIONAL
					}
				}
			]
		]
	] ),
	detailedDescription: new Map<FormValidatorIdentifierEnum, FormValidatorConfigType[]>( [
		[
			FormValidatorIdentifierEnum.MAX_LENGTH,
			[
				{
					identifier: FormValidatorIdentifierEnum.MAX_LENGTH,
					isEnabled: true,
					length: 500,
					variant: {
						conditionIsValid: null,
						type: FormValidatorVariantEnum.CONDITIONAL
					}
				}
			]
		],
		[
			FormValidatorIdentifierEnum.MIN_LENGTH,
			[
				{
					identifier: FormValidatorIdentifierEnum.MIN_LENGTH,
					isEnabled: true,
					length: 1,
					variant: {
						conditionIsValid: null,
						type: FormValidatorVariantEnum.CONDITIONAL
					}
				}
			]
		]
	] ),
	deviceType: new Map<FormValidatorIdentifierEnum, FormValidatorConfigType[]>( [
		[
			FormValidatorIdentifierEnum.REQUIRED,
			[
				{
					identifier: FormValidatorIdentifierEnum.REQUIRED,
					isEnabled: false,
					variant: {
						type: FormValidatorVariantEnum.INITIAL
					}
				}
			]
		]
	] ),
	ersIdentifier: new Map<FormValidatorIdentifierEnum, FormValidatorConfigType[]>( [
		[
			FormValidatorIdentifierEnum.REQUIRED,
			[
				{
					identifier: FormValidatorIdentifierEnum.REQUIRED,
					isEnabled: true,
					variant: {
						type: FormValidatorVariantEnum.INITIAL
					}
				}
			]
		]
	] ),
	ersSerialNumber: new Map<FormValidatorIdentifierEnum, FormValidatorConfigType[]>( [
		[
			FormValidatorIdentifierEnum.MAX_LENGTH,
			[
				{
					identifier: FormValidatorIdentifierEnum.MAX_LENGTH,
					isEnabled: true,
					length: 32,
					variant: {
						type: FormValidatorVariantEnum.INITIAL
					}
				}
			]
		],
		[
			FormValidatorIdentifierEnum.PATTERN,
			[
				{
					identifier: FormValidatorIdentifierEnum.PATTERN,
					isEnabled: true,
					pattern: new RegExp( /^[a-zA-Z0-9()+,'\-.= ]*$/ ),
					variant: {
						type: FormValidatorVariantEnum.INITIAL
					}
				}
			]
		],
		[
			FormValidatorIdentifierEnum.REQUIRED,
			[
				{
					identifier: FormValidatorIdentifierEnum.REQUIRED,
					isEnabled: true,
					variant: {
						type: FormValidatorVariantEnum.INITIAL
					}
				}
			]
		]
	] ),
	model: new Map<FormValidatorIdentifierEnum, FormValidatorConfigType[]>( [
		[
			FormValidatorIdentifierEnum.MAX_LENGTH,
			[
				{
					identifier: FormValidatorIdentifierEnum.MAX_LENGTH,
					isEnabled: true,
					length: 1000,
					variant: {
						type: FormValidatorVariantEnum.INITIAL
					}
				}
			]
		],
		[
			FormValidatorIdentifierEnum.REQUIRED,
			[
				{
					identifier: FormValidatorIdentifierEnum.REQUIRED,
					isEnabled: false,
					variant: {
						type: FormValidatorVariantEnum.INITIAL
					}
				}
			]
		]
	] ),
	software: new Map<FormValidatorIdentifierEnum, FormValidatorConfigType[]>( [
		[
			FormValidatorIdentifierEnum.MAX_LENGTH,
			[
				{
					identifier: FormValidatorIdentifierEnum.MAX_LENGTH,
					isEnabled: true,
					length: 1000,
					variant: {
						type: FormValidatorVariantEnum.INITIAL
					}
				}
			]
		],
		[
			FormValidatorIdentifierEnum.REQUIRED,
			[
				{
					identifier: FormValidatorIdentifierEnum.REQUIRED,
					isEnabled: false,
					variant: {
						type: FormValidatorVariantEnum.INITIAL
					}
				}
			]
		]
	] ),
	status: new Map<FormValidatorIdentifierEnum, FormValidatorConfigType[]>( [
		[
			FormValidatorIdentifierEnum.REQUIRED,
			[
				{
					identifier: FormValidatorIdentifierEnum.REQUIRED,
					isEnabled: false,
					variant: {
						type: FormValidatorVariantEnum.INITIAL
					}
				}
			]
		]
	] ),
	taxOfficeRemark: new Map<FormValidatorIdentifierEnum, FormValidatorConfigType[]>( [
		[
			FormValidatorIdentifierEnum.MAX_LENGTH,
			[
				{
					identifier: FormValidatorIdentifierEnum.MAX_LENGTH,
					isEnabled: true,
					length: 1000,
					variant: {
						conditionIsValid: null,
						type: FormValidatorVariantEnum.CONDITIONAL
					}
				}
			]
		],
		[
			FormValidatorIdentifierEnum.MIN_LENGTH,
			[
				{
					identifier: FormValidatorIdentifierEnum.MIN_LENGTH,
					isEnabled: true,
					length: 3,
					variant: {
						conditionIsValid: null,
						type: FormValidatorVariantEnum.CONDITIONAL
					}
				}
			]
		],
		[
			FormValidatorIdentifierEnum.REQUIRED_CUSTOM,
			[
				{
					identifier: FormValidatorIdentifierEnum.REQUIRED_CUSTOM,
					isEnabled: true,
					errorKey: 'licensePlateNumberRequired',
					variant: {
						conditionIsValid: null,
						type: FormValidatorVariantEnum.CONDITIONAL
					}
				}
			]
		]
	] ),
	tssConnectionDate: new Map<FormValidatorIdentifierEnum, FormValidatorConfigType[]>( [] ),
	tssEntityId: new Map<FormValidatorIdentifierEnum, FormValidatorConfigType[]>( [
		[
			FormValidatorIdentifierEnum.REQUIRED,
			[
				{
					identifier: FormValidatorIdentifierEnum.REQUIRED,
					isEnabled: false,
					variant: {
						type: FormValidatorVariantEnum.INITIAL
					}
				}
			]
		]
	] ),
	version: new Map<FormValidatorIdentifierEnum, FormValidatorConfigType[]>( [
		[
			FormValidatorIdentifierEnum.MAX_LENGTH,
			[
				{
					identifier: FormValidatorIdentifierEnum.MAX_LENGTH,
					isEnabled: true,
					length: 200,
					variant: {
						type: FormValidatorVariantEnum.INITIAL
					}
				}
			]
		]
	] )
};

const validatorsErsExternal: FormValidatorsConfig<ErsFormProp> = {
	acquisitionDate: new Map<FormValidatorIdentifierEnum, FormValidatorConfigType[]>( [
		[
			FormValidatorIdentifierEnum.DATE_RELATION,
			[
				{
					config: null,
					identifier: FormValidatorIdentifierEnum.DATE_RELATION,
					identifierChild: 'activationDateReference',
					isEnabled: true,
					variant: {
						conditionIsValid: null,
						type: FormValidatorVariantEnum.CONDITIONAL
					}
				},
				{
					config: null,
					identifier: FormValidatorIdentifierEnum.DATE_RELATION,
					identifierChild: 'currentDateReference',
					isEnabled: true,
					variant: {
						conditionIsValid: null,
						type: FormValidatorVariantEnum.CONDITIONAL
					}
				}
			]
		],
		[
			FormValidatorIdentifierEnum.REQUIRED,
			[
				{
					identifier: FormValidatorIdentifierEnum.REQUIRED,
					isEnabled: true,
					variant: {
						type: FormValidatorVariantEnum.INITIAL
					}
				}
			]
		]
	] ),
	brand: new Map<FormValidatorIdentifierEnum, FormValidatorConfigType[]>( [
		[
			FormValidatorIdentifierEnum.MAX_LENGTH,
			[
				{
					identifier: FormValidatorIdentifierEnum.MAX_LENGTH,
					isEnabled: true,
					length: 200,
					variant: {
						type: FormValidatorVariantEnum.INITIAL
					}
				}
			]
		],
		[
			FormValidatorIdentifierEnum.REQUIRED,
			[
				{
					identifier: FormValidatorIdentifierEnum.REQUIRED,
					isEnabled: true,
					variant: {
						type: FormValidatorVariantEnum.INITIAL
					}
				}
			]
		]
	] ),
	commissioningDate: new Map<FormValidatorIdentifierEnum, FormValidatorConfigType[]>( [
		// Todo validations are not finally specified yet
		[
			FormValidatorIdentifierEnum.DATE_RELATION,
			[
				{
					config: null,
					identifier: FormValidatorIdentifierEnum.DATE_RELATION,
					identifierChild: 'acquisitionDateReference',
					isEnabled: true,
					variant: {
						conditionIsValid: null,
						type: FormValidatorVariantEnum.CONDITIONAL
					}
				},
				{
					config: null,
					identifier: FormValidatorIdentifierEnum.DATE_RELATION,
					identifierChild: 'currentDateReference',
					isEnabled: true,
					variant: {
						conditionIsValid: null,
						type: FormValidatorVariantEnum.CONDITIONAL
					}
				},
				// conditional validation against deactivationDate as specified in DFDEV-1405
				{
					config: null,
					identifier: FormValidatorIdentifierEnum.DATE_RELATION,
					identifierChild: 'deactivationDateReference',
					isEnabled: true,
					variant: {
						conditionIsValid: null,
						type: FormValidatorVariantEnum.CONDITIONAL
					}
				},
		// 		{
		// 			config: null,
		// 			identifier: FormValidatorIdentifierEnum.DATE_RELATION,
		// 			identifierChild: 'tssConnectionDateReference',
		// 			isEnabled: true,
		// 			variant: {
		// 				conditionIsValid: null,
		// 				type: FormValidatorVariantEnum.CONDITIONAL
		// 			}
		// 		}
			]
		],
		[
			FormValidatorIdentifierEnum.REQUIRED,
			[
				{
					identifier: FormValidatorIdentifierEnum.REQUIRED,
					isEnabled: true,
					variant: {
						conditionIsValid: null,
						type: FormValidatorVariantEnum.CONDITIONAL
					}
				}
			]
		]
	] ),
	deactivationDate: new Map<FormValidatorIdentifierEnum, FormValidatorConfigType[]>( [
		[
			FormValidatorIdentifierEnum.DATE_RELATION,
			[
				{
					config: null,
					identifier: FormValidatorIdentifierEnum.DATE_RELATION,
					identifierChild: 'acquisitionDateReference',
					isEnabled: true,
					variant: {
						conditionIsValid: null,
						type: FormValidatorVariantEnum.CONDITIONAL
					}
				},
				{
					config: null,
					identifier: FormValidatorIdentifierEnum.DATE_RELATION,
					identifierChild: 'activationDateReference',
					isEnabled: true,
					variant: {
						conditionIsValid: null,
						type: FormValidatorVariantEnum.CONDITIONAL
					}
				},
				{
					config: null,
					identifier: FormValidatorIdentifierEnum.DATE_RELATION,
					identifierChild: 'commissioningDateReference',
					isEnabled: true,
					variant: {
						conditionIsValid: null,
						type: FormValidatorVariantEnum.CONDITIONAL
					}
				},
				{
					config: null,
					identifier: FormValidatorIdentifierEnum.DATE_RELATION,
					identifierChild: 'currentDateReference',
					isEnabled: true,
					variant: {
						conditionIsValid: null,
						type: FormValidatorVariantEnum.CONDITIONAL
					}
				}
			]
		],
		[
			FormValidatorIdentifierEnum.REQUIRED,
			[
				{
					identifier: FormValidatorIdentifierEnum.REQUIRED,
					isEnabled: true,
					variant: {
						conditionIsValid: null,
						type: FormValidatorVariantEnum.CONDITIONAL
					}
				}
			]
		]
	] ),
	deactivationReason: new Map<FormValidatorIdentifierEnum, FormValidatorConfigType[]>( [
		[
			FormValidatorIdentifierEnum.MAX_LENGTH,
			[
				{
					identifier: FormValidatorIdentifierEnum.MAX_LENGTH,
					isEnabled: true,
					length: 1000,
					variant: {
						conditionIsValid: null,
						type: FormValidatorVariantEnum.CONDITIONAL
					}
				}
			]
		],
		[
			FormValidatorIdentifierEnum.MIN_LENGTH,
			[
				{
					identifier: FormValidatorIdentifierEnum.MIN_LENGTH,
					isEnabled: true,
					length: 1,
					variant: {
						conditionIsValid: null,
						type: FormValidatorVariantEnum.CONDITIONAL
					}
				}
			]
		]
	] ),
	decommissionDate: new Map<FormValidatorIdentifierEnum, FormValidatorConfigType[]>( [] ),
	detailedDescription: new Map<FormValidatorIdentifierEnum, FormValidatorConfigType[]>( [
		[
			FormValidatorIdentifierEnum.MAX_LENGTH,
			[
				{
					identifier: FormValidatorIdentifierEnum.MAX_LENGTH,
					isEnabled: true,
					length: 500,
					variant: {
						conditionIsValid: null,
						type: FormValidatorVariantEnum.CONDITIONAL
					}
				}
			]
		],
		[
			FormValidatorIdentifierEnum.MIN_LENGTH,
			[
				{
					identifier: FormValidatorIdentifierEnum.MIN_LENGTH,
					isEnabled: true,
					length: 1,
					variant: {
						conditionIsValid: null,
						type: FormValidatorVariantEnum.CONDITIONAL
					}
				}
			]
		]
	] ),
	deviceType: new Map<FormValidatorIdentifierEnum, FormValidatorConfigType[]>( [
		[
			FormValidatorIdentifierEnum.REQUIRED,
			[
				{
					identifier: FormValidatorIdentifierEnum.REQUIRED,
					isEnabled: true,
					variant: {
						type: FormValidatorVariantEnum.INITIAL
					}
				}
			]
		]
	] ),
	ersIdentifier: new Map<FormValidatorIdentifierEnum, FormValidatorConfigType[]>( [
		[
			FormValidatorIdentifierEnum.REQUIRED,
			[
				{
					identifier: FormValidatorIdentifierEnum.REQUIRED,
					isEnabled: true,
					variant: {
						type: FormValidatorVariantEnum.INITIAL
					}
				}
			]
		]
	] ),
	ersSerialNumber: new Map<FormValidatorIdentifierEnum, FormValidatorConfigType[]>( [
		[
			FormValidatorIdentifierEnum.MAX_LENGTH,
			[
				{
					identifier: FormValidatorIdentifierEnum.MAX_LENGTH,
					isEnabled: true,
					length: 32,
					variant: {
						type: FormValidatorVariantEnum.INITIAL
					}
				}
			]
		],
		[
			FormValidatorIdentifierEnum.PATTERN,
			[
				{
					identifier: FormValidatorIdentifierEnum.PATTERN,
					isEnabled: true,
					pattern: new RegExp( /^[a-zA-Z0-9()+,'\-.= ]*$/ ),
					variant: {
						type: FormValidatorVariantEnum.INITIAL
					}
				}
			]
		],
		[
			FormValidatorIdentifierEnum.REQUIRED,
			[
				{
					identifier: FormValidatorIdentifierEnum.REQUIRED,
					isEnabled: true,
					variant: {
						type: FormValidatorVariantEnum.INITIAL
					}
				}
			]
		]
	] ),
	model: new Map<FormValidatorIdentifierEnum, FormValidatorConfigType[]>( [
		[
			FormValidatorIdentifierEnum.MAX_LENGTH,
			[
				{
					identifier: FormValidatorIdentifierEnum.MAX_LENGTH,
					isEnabled: true,
					length: 1000,
					variant: {
						type: FormValidatorVariantEnum.INITIAL
					}
				}
			]
		],
		[
			FormValidatorIdentifierEnum.REQUIRED,
			[
				{
					identifier: FormValidatorIdentifierEnum.REQUIRED,
					isEnabled: true,
					variant: {
						type: FormValidatorVariantEnum.INITIAL
					}
				}
			]
		]
	] ),
	software: new Map<FormValidatorIdentifierEnum, FormValidatorConfigType[]>( [
		[
			FormValidatorIdentifierEnum.MAX_LENGTH,
			[
				{
					identifier: FormValidatorIdentifierEnum.MAX_LENGTH,
					isEnabled: true,
					length: 1000,
					variant: {
						type: FormValidatorVariantEnum.INITIAL
					}
				}
			]
		],
		[
			FormValidatorIdentifierEnum.REQUIRED,
			[
				{
					identifier: FormValidatorIdentifierEnum.REQUIRED,
					isEnabled: true,
					variant: {
						type: FormValidatorVariantEnum.INITIAL
					}
				}
			]
		]
	] ),
	status: new Map<FormValidatorIdentifierEnum, FormValidatorConfigType[]>( [
		[
			FormValidatorIdentifierEnum.REQUIRED,
			[
				{
					identifier: FormValidatorIdentifierEnum.REQUIRED,
					isEnabled: true,
					variant: {
						type: FormValidatorVariantEnum.INITIAL
					}
				}
			]
		]
	] ),
	taxOfficeRemark: new Map<FormValidatorIdentifierEnum, FormValidatorConfigType[]>( [
		[
			FormValidatorIdentifierEnum.MAX_LENGTH,
			[
				{
					identifier: FormValidatorIdentifierEnum.MAX_LENGTH,
					isEnabled: true,
					length: 1000,
					variant: {
						conditionIsValid: null,
						type: FormValidatorVariantEnum.CONDITIONAL
					}
				}
			]
		],
		[
			FormValidatorIdentifierEnum.MIN_LENGTH,
			[
				{
					identifier: FormValidatorIdentifierEnum.MIN_LENGTH,
					isEnabled: true,
					length: 3,
					variant: {
						conditionIsValid: null,
						type: FormValidatorVariantEnum.CONDITIONAL
					}
				}
			]
		],
		[
			FormValidatorIdentifierEnum.REQUIRED_CUSTOM,
			[
				{
					identifier: FormValidatorIdentifierEnum.REQUIRED_CUSTOM,
					isEnabled: true,
					errorKey: 'licensePlateNumberRequired',
					variant: {
						conditionIsValid: null,
						type: FormValidatorVariantEnum.CONDITIONAL
					}
				}
			]
		]
	] ),
	tssConnectionDate: new Map<FormValidatorIdentifierEnum, FormValidatorConfigType[]>( [
		[
			FormValidatorIdentifierEnum.DATE_RELATION,
			[
				{
					config: null,
					identifier: FormValidatorIdentifierEnum.DATE_RELATION,
					identifierChild: 'currentDateReference',
					isEnabled: true,
					variant: {
						conditionIsValid: null,
						type: FormValidatorVariantEnum.CONDITIONAL
					}
				}
			]
		],
		[
			FormValidatorIdentifierEnum.REQUIRED,
			[
				{
					identifier: FormValidatorIdentifierEnum.REQUIRED,
					isEnabled: true,
					variant: {
						conditionIsValid: null,
						type: FormValidatorVariantEnum.CONDITIONAL
					}
				}
			]
		]
	] ),
	tssEntityId: new Map<FormValidatorIdentifierEnum, FormValidatorConfigType[]>( [
		[
			FormValidatorIdentifierEnum.REQUIRED,
			[
				{
					identifier: FormValidatorIdentifierEnum.REQUIRED,
					isEnabled: true,
					variant: {
						conditionIsValid: null,
						type: FormValidatorVariantEnum.CONDITIONAL
					}
				}
			]
		]
	] ),
	version: new Map<FormValidatorIdentifierEnum, FormValidatorConfigType[]>( [
		[
			FormValidatorIdentifierEnum.MAX_LENGTH,
			[
				{
					identifier: FormValidatorIdentifierEnum.MAX_LENGTH,
					isEnabled: true,
					length: 200,
					variant: {
						type: FormValidatorVariantEnum.INITIAL
					}
				}
			]
		]
	] )
};

if ( environment.features.elster.enabled ) {
	supportedEntityVariants.push( EntityVariantEnum.EXTERNAL );
}


export const ERS_METADATA: IErsMetadata = ObjectUtils.deepCopyByRecursion<IErsMetadata>( {
	buttonGroupConfigs: {
		list: {
			items: listButtonGroupItems?.filter(
				( buttonGroupItemType: ButtonGroupItemType ) => supportedEntityVariants.includes( buttonGroupItemType.id as EntityVariantEnum )
			)
		}
	},
	entityId: APP_CONFIG.ers.entityId,
	details: {
		dateFormat: { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false },
		internalTabContent: [
			{
				key: 'tenantId',
				order: 1,
				i18nPath: 'ers.details.tenantId',
				addSpaceToPrevious: false,
				showOnDevices: [ DeviceTypeEnum.MOBILE, DeviceTypeEnum.TABLET, DeviceTypeEnum.DESKTOP ],
				copyClipboard: true
			},
			{
				key: 'organizationId',
				order: 2,
				i18nPath: 'ers.details.organizationId',
				addSpaceToPrevious: false,
				showOnDevices: [ DeviceTypeEnum.MOBILE, DeviceTypeEnum.TABLET, DeviceTypeEnum.DESKTOP ],
				copyClipboard: true
			},
			{
				key: 'businessPremisesId',
				order: 3,
				i18nPath: 'ers.details.businessPremisesId',
				addSpaceToPrevious: false,
				showOnDevices: [ DeviceTypeEnum.MOBILE, DeviceTypeEnum.TABLET, DeviceTypeEnum.DESKTOP ],
				copyClipboard: true
			},
			{
				key: 'tssEntityId',
				order: 4,
				i18nPath: 'ers.details.tssId',
				addSpaceToPrevious: false,
				showOnDevices: [ DeviceTypeEnum.MOBILE, DeviceTypeEnum.TABLET, DeviceTypeEnum.DESKTOP ],
				copyClipboard: true
			},
			{
				key: 'id',
				order: 5,
				i18nPath: 'ers.details.ersId',
				addSpaceToPrevious: false,
				showOnDevices: [ DeviceTypeEnum.MOBILE, DeviceTypeEnum.TABLET, DeviceTypeEnum.DESKTOP ],
				copyClipboard: true
			}
		]
	},
	form: {
		baseData: {
			acquisitionDate: {
				addSpaceToPrevious: true,
				elsterField: true,
				elsterRequiredField: true,
				elsterShowIndicator: true,
				hide: false,
				i18nPath: 'ers.form.acquisitionDate',
				order: 11,
				showInfo: true,
				specialTreatments: [ FormDataTreatmentEnum.DATE_TO_LOCALE_STRING ]
			},
			brand: {
				addSpaceToPrevious: false,
				elsterField: true,
				elsterRequiredField: true,
				elsterShowIndicator: true,
				hide: false,
				i18nPath: 'ers.form.brand',
				order: 5,
				showInfo: true,
				specialTreatments: null
			},
			businessPremisesId: {
				hide: true,
				i18nPath: '',
				order: null,
				specialTreatments: null
			},
			commissioningDate: {
				addSpaceToPrevious: true,
				elsterField: true,
				elsterRequiredCondition: isStatusActive,
				elsterShowIndicator: true,
				hide: false,
				i18nPath: 'ers.form.commissioningDate',
				order: 12,
				showInfo: true,
				specialTreatments: [ FormDataTreatmentEnum.DATE_TO_LOCALE_STRING ]
			},
			deactivationDate: {
				addSpaceToPrevious: false,
				elsterField: true,
				elsterRequiredCondition: isStatusDisabled,
				elsterShowIndicator: true,
				hide: false,
				i18nPath: 'ers.form.deactivationDate',
				order: 15,
				showInfo: true,
				specialTreatments: [ FormDataTreatmentEnum.DATE_TO_LOCALE_STRING ]
			},
			deactivationReason: {
				addSpaceToPrevious: false,
				elsterField: true,
				elsterShowIndicator: true,
				hide: false,
				i18nPath: 'ers.form.deactivationReason',
				maxLength: 1000,
				order: 16,
				permissionData: {
					scopes: [ FeatureScopesEnum.ERIC ],
					permissionCheck: ScopePermissionCheckEnum.AND
				},
				showInfo: true,
				specialTreatments: [ FormDataTreatmentEnum.HAS_PERMISSION ]
			},
			decommissionDate: {
				addSpaceToPrevious: false,
				elsterField: true,
				elsterRequiredCondition: isStatusDisabled,
				elsterShowIndicator: true,
				hide: false,
				i18nPath: 'ers.form.decommissionDate',
				order: 14,
				showInfo: true,
				specialTreatments: [ FormDataTreatmentEnum.DATE_TO_LOCALE_STRING ]
			},
			detailedDescription: {
				addSpaceToPrevious: false,
				hide: false,
				i18nPath: 'shared.forms.detailedDescription',
				order: 3,
				showInfo: true,
				specialTreatments: null
			},
			deviceType: {
				addSpaceToPrevious: true,
				elsterField: true,
				elsterRequiredField: true,
				elsterShowIndicator: true,
				hide: false,
				i18nPath: 'ers.form.deviceType',
				order: 4,
				permissionData: {
					scopes: [ FeatureScopesEnum.ERIC ],
					permissionCheck: ScopePermissionCheckEnum.AND
				},
				specialTreatments: [
					FormDataTreatmentEnum.ERS_DEVICE_TYPE_TO_I18N,
					FormDataTreatmentEnum.HAS_PERMISSION
				]
			},
			ersIdentifier: {
				addSpaceToPrevious: false,
				hide: false,
				i18nPath: 'ers.form.ersIdentifier',
				order: 1,
				showInfo: true,
				specialTreatments: null
			},
			ersSerialNumber: {
				addSpaceToPrevious: false,
				elsterField: true,
				elsterRequiredField: true,
				elsterShowIndicator: true,
				hide: false,
				i18nPath: 'ers.form.ersSerialNumber',
				order: 2,
				showInfo: true,
				specialTreatments: null
			},
			id: {
				hide: true,
				i18nPath: '',
				order: null,
				specialTreatments: null
			},
			model: {
				addSpaceToPrevious: false,
				elsterField: true,
				elsterRequiredField: true,
				elsterShowIndicator: true,
				hide: false,
				i18nPath: 'ers.form.model',
				order: 6,
				permissionData: {
					scopes: [ FeatureScopesEnum.ERIC ],
					permissionCheck: ScopePermissionCheckEnum.AND
				},
				specialTreatments: [ FormDataTreatmentEnum.HAS_PERMISSION ]
			},
			software: {
				addSpaceToPrevious: false,
				elsterField: true,
				elsterRequiredField: true,
				elsterShowIndicator: true,
				hide: false,
				i18nPath: 'ers.form.software',
				order: 7,
				permissionData: {
					scopes: [ FeatureScopesEnum.ERIC ],
					permissionCheck: ScopePermissionCheckEnum.AND
				},
				specialTreatments: [ FormDataTreatmentEnum.HAS_PERMISSION ]
			},
			status: {
				hide: false,
				i18nPath: 'shared.forms.status',
				order: 9,
				specialTreatments: [ FormDataTreatmentEnum.STATUS_TO_I18N ]
			},
			taxOfficeRemark: {
				addSpaceToPrevious: true,
				elsterField: true,
				elsterRequiredCondition: isDeviceTypeTaximeterOrDistanceCounter,
				elsterShowIndicator: true,
				hide: false,
				i18nPath: 'ers.form.taxOfficeRemark',
				maxLength: 1000,
				order: 17,
				permissionData: {
					scopes: [ FeatureScopesEnum.ERIC ],
					permissionCheck: ScopePermissionCheckEnum.AND
				},
				specialTreatments: [ FormDataTreatmentEnum.HAS_PERMISSION ]
			},
			tssConnectionDate: {
				addSpaceToPrevious: true,
				elsterField: true,
				elsterRequiredCondition: isStatusActive,
				elsterShowIndicator: true,
				hide: false,
				i18nPath: 'ers.form.tssConnectionDate',
				order: 13,
				showInfo: true,
				specialTreatments: [ FormDataTreatmentEnum.DATE_TO_LOCALE_STRING ]
			},
			tssEntityId: {
				addSpaceToPrevious: false,
				hide: false,
				i18nPath: 'ers.form.tssEntityId',
				order: 10,
				specialTreatments: null
			},
			// additional property for differentiation between ERSes on customer side
			type: {
				hide: true,
				i18nPath: null,
				order: null,
				specialTreatments: null
			},
			version: {
				addSpaceToPrevious: false,
				elsterField: true,
				elsterShowIndicator: true,
				hide: false,
				i18nPath: 'ers.form.version',
				order: 8,
				showInfo: true,
				specialTreatments: null
			}
		},
		createData: [],
		preserveData: [
			{ key: 'businessPremisesId', fallbackValue: null },
			{ key: 'ersSerialNumber', fallbackValue: null },
			{ key: 'etag', fallbackValue: null },
			{ key: 'id', fallbackValue: null },
			{ key: 'status', fallbackValue: null },
			{ key: 'type', fallbackValue: null },
			{ key: 'tssEntityId', fallbackValue: null }
		],
		updateData: [],
		validatorsErs: validatorsErs,
		validatorsErsExternal: validatorsErsExternal
	},
	i18n: {
		basepath: APP_CONFIG.ers.i18nPath,
		page: 'page',
		tab: 'tab',
		entity: 'entity',
		form: 'form',
		details: 'details',
		ersLifecycleModal: 'modals.ersLifecycle'
	},
	listOperations: listOperationsConfigErs( ListOperationsIdentifierEnum.ERS_LIST_PAGE ),
	listOperationsExternal: listOperationsConfigErsExternal( ListOperationsIdentifierEnum.ERS_EXTERNAL_LIST_PAGE ),
	listOperationsBusinessPremisesErs: listOperationsConfigErs( ListOperationsIdentifierEnum.BUSINESS_PREMISES_DETAIL_PAGE_FISKAL_ERSES ),
	listOperationsBusinessPremisesErsExternal: listOperationsConfigErsExternal(
		ListOperationsIdentifierEnum.BUSINESS_PREMISES_DETAIL_PAGE_FISKAL_ERSES_EXTERNAL ),
	status: {
		actions: [
			{
				actionItem: {
					action: ActionEnum.ACTIVATE,
					actionItemIcon: ActionEnum.REACTIVATE,
					labelI18n: 'shared.buttons.reactivate',
					tooltipI18n: 'shared.buttons.reactivate',
					scopes: ErsScopesEnum.StatusEnable
				},
				dialog: {
					i18nPathButtonConfirm: 'reactivate',
					i18nPathDescription: 'ers.entity.dialogForReactivate.description'
				},
				enforceDisableOnStatuses: [],
				enforceHideOnStatuses: [ ErsStatusEnum.DISABLED, ErsStatusEnum.INACTIVE ],
				order: 1,
				outOfFlowDisplayMode: OutOfFlowDisplayModeEnum.DISABLED,
				requestActionStatus: actionStatus.REACTIVATE,
				targetStatus: ErsStatusEnum.ACTIVE
			},
			{
				actionItem: {
					action: ActionEnum.DISABLE,
					labelI18n: 'shared.buttons.disable',
					tooltipI18n: 'shared.buttons.disable',
					scopes: ErsScopesEnum.StatusDisable
				},
				dialog: {
					i18nPathButtonConfirm: 'disable',
					i18nPathDescription: 'ers.entity.dialogForDisabled.description'
				},
				enforceDisableOnStatuses: [],
				enforceHideOnStatuses: [ ErsStatusEnum.INACTIVE ],
				order: 3,
				outOfFlowDisplayMode: OutOfFlowDisplayModeEnum.DISABLED,
				requestActionStatus: actionStatus.DISABLE,
				targetStatus: ErsStatusEnum.DISABLED
			},
			{
				actionItem: {
					action: ActionEnum.SUSPEND,
					labelI18n: 'shared.buttons.suspend',
					tooltipI18n: 'shared.buttons.suspend',
					scopes: ErsScopesEnum.StatusSuspend
				},
				dialog: {
					i18nPathButtonConfirm: 'suspend',
					i18nPathDescription: 'ers.entity.dialogForSuspend.description'
				},
				enforceDisableOnStatuses: [],
				enforceHideOnStatuses: [ ErsStatusEnum.DISABLED, ErsStatusEnum.INACTIVE ],
				order: 2,
				outOfFlowDisplayMode: OutOfFlowDisplayModeEnum.DISABLED,
				requestActionStatus: actionStatus.SUSPEND,
				targetStatus: ErsStatusEnum.SUSPENDED
			}
		],
		flow: {
			[ ErsStatusEnum.ACTIVE ]: [ ErsStatusEnum.DISABLED, ErsStatusEnum.SUSPENDED ],
			[ ErsStatusEnum.DELETED ]: [],
			[ ErsStatusEnum.DISABLED ]: [ ErsStatusEnum.DELETED, ErsStatusEnum.INACTIVE ],
			[ ErsStatusEnum.INACTIVE ]: [ ErsStatusEnum.DELETED ],
			[ ErsStatusEnum.SUSPENDED ]: [ ErsStatusEnum.ACTIVE, ErsStatusEnum.DISABLED ]
		}
	},
	statusErsExternal: {
		actions: [
			{
				actionItem: {
					action: ActionEnum.ACTIVATE,
					actionItemIcon: ActionEnum.REACTIVATE,
					labelI18n: 'shared.buttons.activate',
					tooltipI18n: 'shared.buttons.activate',
					scopes: ErsScopesEnum.StatusEnable
				},
				dialog: {
					i18nPathButtonConfirm: 'activate',
					i18nPathDescription: 'ers.entity.dialogForActivate.description'
				},
				enforceDisableOnStatuses: [],
				enforceHideOnStatuses: [],
				order: 1,
				outOfFlowDisplayMode: OutOfFlowDisplayModeEnum.DISABLED,
				requestActionStatus: actionStatus.REACTIVATE,
				targetStatus: ErsExternalStatusEnum.ACTIVE
			},
			{
				actionItem: {
					action: ActionEnum.DISABLE,
					labelI18n: 'shared.buttons.disable',
					tooltipI18n: 'shared.buttons.disable',
					scopes: ErsScopesEnum.StatusDisable
				},
				dialog: {
					i18nPathButtonConfirm: 'disable',
					i18nPathDescription: 'ers.entity.dialogForDisabled.description'
				},
				enforceDisableOnStatuses: [],
				enforceHideOnStatuses: [ ErsExternalStatusEnum.INACTIVE ],
				order: 3,
				outOfFlowDisplayMode: OutOfFlowDisplayModeEnum.DISABLED,
				requestActionStatus: actionStatus.DISABLE,
				targetStatus: ErsExternalStatusEnum.DISABLED
			}
		],
		flow: {
			[ ErsExternalStatusEnum.ACTIVE ]: [ ErsExternalStatusEnum.DISABLED ],
			[ ErsExternalStatusEnum.DELETED ]: [],
			[ ErsExternalStatusEnum.DISABLED ]: [ ErsExternalStatusEnum.ACTIVE, ErsExternalStatusEnum.DELETED ],
			[ ErsExternalStatusEnum.INACTIVE ]: [ ErsExternalStatusEnum.ACTIVE, ErsExternalStatusEnum.DELETED ]
		}
	},
	storeId: APP_CONFIG.ers.store.id,
	supportedEntityVariants
} );
